import React from 'react';
import { Tooltip, IconButton } from '@mui/material';
import {
  Description as DescriptionIcon,
  Image as ImageIcon,
  VideoLibrary as VideoLibraryIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Article as ArticleIcon,
  Slideshow as SlideshowIcon,
  TableChart as TableChartIcon,
  Archive as ArchiveIcon,
  Folder as FolderIcon,
} from '@mui/icons-material';
import { useTheme } from '@emotion/react';
import { tokens } from '../theme';

// Derive a friendly file/evidence type from an evidence entity
export function deriveEvidenceType(ev) {
  const t = String(ev?.evidence_type || '').toLowerCase();
  const name = String(ev?.file_name || ev?.evidence_url || '').toLowerCase();
  const ext = name.includes('.') ? name.split('.').pop() : '';
  if (t.startsWith('image')) return 'image';
  if (t.startsWith('video')) return 'video';
  if (ext === 'pdf') return 'pdf';
  if (['doc', 'docx'].includes(ext)) return 'docx';
  if (['ppt', 'pptx'].includes(ext)) return 'pptx';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return 'xlsx';
  if (['zip', 'rar', '7z'].includes(ext)) return 'zip';
  if (t.includes('folder') || (!ext && !t)) return 'folder';
  return 'default';
}

function getTypeColor(type, colors) {
  switch (type) {
    case 'pdf': return colors.redAccent[400];
    case 'image': return colors.Blue[400];
    case 'video': return colors.purple[400];
    case 'folder': return colors.Orange[400];
    case 'docx': return colors.Blue[400];
    case 'pptx': return colors.Orange[400];
    case 'xlsx': return colors.green[400];
    case 'zip': return colors.grey[500];
    default: return colors.grey[500];
  }
}

function renderIconByType(type, sx) {
  switch (type) {
    case 'image': return <ImageIcon fontSize="small" sx={sx} />;
    case 'video': return <VideoLibraryIcon fontSize="small" sx={sx} />;
    case 'pdf': return <DescriptionIcon fontSize="small" sx={sx} />;
    case 'docx': return <ArticleIcon fontSize="small" sx={sx} />;
    case 'pptx': return <SlideshowIcon fontSize="small" sx={sx} />;
    case 'xlsx': return <TableChartIcon fontSize="small" sx={sx} />;
    case 'zip': return <ArchiveIcon fontSize="small" sx={sx} />;
    case 'folder': return <FolderIcon fontSize="small" sx={sx} />;
    default: return <InsertDriveFileIcon fontSize="small" sx={sx} />;
  }
}

/**
 * EvidenceFileIcon
 * Props:
 *  - evidence: object (with file_name/evidence_url/evidence_type)
 *  - onClick: function (optional). If provided, wraps icon in IconButton
 *  - title: string tooltip title (optional)
 */
export function EvidenceFileIcon({ evidence, onClick, title }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const type = deriveEvidenceType(evidence);
  const color = getTypeColor(type, colors);
  const icon = renderIconByType(type, { color });

  const content = onClick ? (
    <IconButton size="small" onClick={onClick}>
      {icon}
    </IconButton>
  ) : icon;

  return title ? (
    <Tooltip title={title}>
      <span>
        {content}
      </span>
    </Tooltip>
  ) : content;
}

export default EvidenceFileIcon;
